import {useContext, useEffect, useRef} from 'react';
import {matchPath, useLocation} from 'react-router-dom';

import {AlchemyAnalytics} from '@amzn/alchemy-components/dist/utils/';

import {APIGatewayClientContext} from '../provider/APIGatewayClientProvider';
import {WarehouseIdsContext} from '../provider/WarehouseIdsProvider';
import {WAREHOUSE_ID_URL} from '../router/AppRouter';

/**
 * Initializes Pendo product analytics via the AFT Alchemy Analytics wrapper.
 *
 * The user's login/alias, warehouseId and realm, namespaced as account id `IBRodeo_<warehouseId>`.
 * The wrapper hashes the visitor id for TPS compliance and self-injects the Pendo agent. See:
 * https://quip-amazon.com/ARKCAYaFukqC
 *
 * This is a single-page app initializing once and then calling the wrapper's `updateOptions`
 *  whenever the URL's warehouse changes, keeping the reported FC in sync with the page the user is viewing.
 *
 * Renders nothing; it exists only for these side effects.
 */

// Public Pendo application key
const PENDO_API_KEY = 'a2cdf45e-57e6-4fa2-89f6-d7cf786a275b';

// The wrapper's `dev` mode logs to the console instead of sending to Pendo, so deployed
// stages report real data while local development does not.
// Note: AFTX's CloudFront CSP does not currently allow the Pendo agent script, so on AFTX
// analytics work only once the CSP allowlists content.pendo.aft.amazon.dev.
function isProduction() {
    const hostname = window.location.hostname;
    if (hostname.indexOf('localhost') !== -1) {
        return false;
    }
    return hostname.endsWith('amazonoperations.app')
        || hostname.endsWith('inbound.rodeo.aft.a2z.com');
}

// Resolves the warehouse currently being viewed from the URL path and only trust it if it is
// a known warehouse. Falls back to 'UNKNOWN'.
function getCurrentWarehouseId(pathname, warehouseIds) {
    const match = matchPath(pathname, {path: WAREHOUSE_ID_URL, exact: false, strict: false});
    const candidate = match && match.params.warehouseId;
    if (candidate && (warehouseIds || []).includes(candidate)) {
        return candidate;
    }
    return 'UNKNOWN';
}

export default function PendoAnalytics() {
    const {client} = useContext(APIGatewayClientContext);
    const {warehouseIds} = useContext(WarehouseIdsContext);
    const location = useLocation();

    // The initialized AlchemyAnalytics instance, and the fetched realm/login retained so
    // navigation updates don't re-fetch them.
    const analyticsRef = useRef(null);
    const metaRef = useRef({realm: 'UNKNOWN', remoteUser: undefined});

    const currentWarehouseId = getCurrentWarehouseId(location.pathname, warehouseIds);

    // Held in a ref so the async init below reads the latest FC at resolve time, avoiding
    // a race where the user navigates before getRealm/getIdentity resolve.
    const latestWarehouseId = useRef(currentWarehouseId);
    latestWarehouseId.current = currentWarehouseId;

    // One-time initialization, once the API client is available. The wrapper is constructed
    // on every domain (mirroring OB Rodeo); `dev` decides whether data is actually sent.
    useEffect(() => {
        if (analyticsRef.current || !client) {
            return;
        }
        let cancelled = false;

        Promise.all([client.getRealm(), client.getIdentity()])
            .then(([realmResponse, identityResponse]) => {
                if (cancelled || analyticsRef.current) {
                    return;
                }
                const realm = realmResponse?.data?.realm || 'UNKNOWN';
                const remoteUser = identityResponse?.data?.username || undefined;
                metaRef.current = {realm, remoteUser};

                const warehouseId = latestWarehouseId.current;
                analyticsRef.current = new AlchemyAnalytics({
                    apikey: PENDO_API_KEY,
                    account: {id: `IBRodeo_${warehouseId}`, IBRodeo_realm: realm},
                    visitor: {id: remoteUser, warehouseId, realm},
                    location: {
                        transforms: [{attr: 'search', action: 'ExcludeKeys', data: ['employeeId', 'remoteUser']}]
                    },
                    dev: !isProduction(),
                    excludeAllText: true
                });
            })
            .catch(() => {
                // Prevents breaking the app on an analytics update.
            });

        return () => {
            cancelled = true;
        };
    }, [client]);

    // On navigation to a different warehouse, update Pendo's metadata.
    // No-ops until initialization has completed.
    useEffect(() => {
        const analytics = analyticsRef.current;
        if (!analytics) {
            return;
        }
        const {realm, remoteUser} = metaRef.current;
        Promise.resolve(analytics.updateOptions({
            account: {id: `IBRodeo_${currentWarehouseId}`, IBRodeo_realm: realm},
            visitor: {id: remoteUser, warehouseId: currentWarehouseId, realm}
        })).catch(() => {
            // Prevents breaking the app on an analytics update.
        });
    }, [currentWarehouseId]);

    return null;
}
